<!-- Responsibility: Implement companion chat dialog behavior for the ui/components/dialog surface.
Scope: Owns ui/components/dialog concerns only; cross-surface orchestration is excluded. -->
<companion-chat-dialog>
  <div class="chat-content-wrap">
    <header if={ state.companion } class="float-media">
      <div data-role="portrait">
        <img if={ state.companion.portraitUrl } src={ state.companion.portraitUrl } alt={ state.companion.name }>
        <span if={ !state.companion.portraitUrl }>{ state.companion.name?.[0]?.toUpperCase() }</span>
      </div>
      <div data-role="identity">
        <h2 class="fm-chat-title">{ state.companion.name }</h2>
        <small>{ state.companion.identity }</small>
      </div>
    </header>

    <ul ref="history" class="chat-history">
      <li each={ entry in state.conversations } data-from={ entry.from === state.playerName ? 'player' : 'companion' }>
        <small class="msg-from">{ entry.from }</small>
        <p class="msg-text">{ entry.text }</p>
        <time class="msg-time">{ entry.gameClock }</time>
      </li>
      <li if={ state.busy } data-role="typing">{ state.companion?.name } is thinking…</li>
      <li if={ state.error } data-role="error">{ state.error }</li>
    </ul>
  </div>

  <form onsubmit={ send } class="chat-input-form">
    <input
      ref="chatInput"
      type="text"
      placeholder="Say something…"
      disabled={ state.busy }
      autocomplete="off"
    />
    <button type="submit" disabled={ state.busy }>SEND</button>
  </form>

  <script>
    import { dispatcher } from '../../../app-states.js'
    import { TOPICS } from "@anyquest/shared-core"
    import { formatAIErrorMessage } from '../../../ai/errors.js'
    import * as gameStore from '../../../game-store.js'

    export default {
      state: {
        playerName: 'Player',
        busy:       false,
        error:      '',
        companion:  null,
        conversations: [],
      },

      onMounted() {
        this.syncState(this.props)
        this.update()
        
        this.handles = [
          dispatcher.on(TOPICS.companion.chatUpdated, (_e, d) => {
            if (d?.companionName === this.state.companion?.name) {
              const phase = d?.phase
              const terminal = phase === 'companion' || phase === 'error' || !phase
              
              // Re-sync companion data from store
              this.syncState(this.props)
              
              this.update({
                busy: terminal ? false : this.state.busy,
                error: phase === 'error' ? (d?.error || this.state.error) : '',
              })
              this.scrollHistory()
            }
          }),
        ]

        requestAnimationFrame(() => {
          this.scrollHistory()
          this.$('[ref="chatInput"]')?.focus()
        })
      },

      onBeforeUpdate(props) {
        this.syncState(props)
      },

      onUnmounted() {
        this.handles?.forEach(h => dispatcher.off(h))
      },

      syncState(props) {
        const playerName = gameStore.state?.player?.identity || 'Player'
        const name = props.content?.companionName || props.content?.companion?.name
        const companion = gameStore.state?.companions?.find(c => c.name === name) || props.content?.companion
        
        Object.assign(this.state, {
          playerName,
          companion,
          conversations: companion?.conversations || []
        })
      },

      async send(e) {
        e.preventDefault()
        const input = this.$('[ref="chatInput"]')
        const text = input?.value?.trim()
        const comp = this.state.companion
        if (!text || this.state.busy || !comp) return

        input.value = ''
        this.update({ busy: true, error: '' })
        try {
          await gameStore.talkToCompanion(comp.name, text)
          // chatUpdated event will clear busy + update companion via syncState
        } catch (err) {
          console.error('[CompanionChat] send failed:', err)
          this.update({ busy: false, error: formatAIErrorMessage(err) })
        }
      },

      scrollHistory() {
        const el = this.$('[ref="history"]')
        if (el) el.scrollTop = el.scrollHeight
      },
    }
  </script>
  
  <style>
    companion-chat-dialog {
      display: flex;
      flex-direction: column;
      gap: var(--space-md);
      height: 100%;
    }

    .chat-content-wrap {
      display: block; /* For float */
      overflow: auto;
      flex: 1;
    }

    .float-media {
      float: left;
      width: 40% !important;
      margin-right: var(--space-md);
      margin-bottom: var(--space-sm);
      border: 1px solid var(--border);
      border-radius: 0.5rem;
      overflow: hidden;
      background: var(--surface-card-3);
      padding: var(--space-xs);
    }

    .float-media [data-role="portrait"] {
      width: 100%;
      height: auto;
      aspect-ratio: 1;
      display: flex;
      align-items: center;
      justify-content: center;
      overflow: hidden;
      border-radius: 0.25rem;
      background: var(--bg);
    }

    .float-media [data-role="portrait"] img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .float-media [data-role="portrait"] span {
      font-size: var(--type-2xl);
      font-weight: 800;
      color: var(--gold);
    }

    .float-media [data-role="identity"] {
      padding: var(--space-xs) var(--space-xs);
      text-align: center;
    }

    .fm-chat-title {
      margin: 0;
      font-size: var(--type-sm);
      color: var(--gold);
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .float-media small {
      font-size: var(--type-3xs);
      color: var(--text-dim);
      display: block;
      margin-top: 0.125rem;
    }

    .chat-history {
      flex: 1;
      overflow-y: auto;
      display: flex;
      flex-direction: column;
      gap: var(--space-sm);
      list-style: none;
      margin: 0;
      padding: 0;
    }

    .chat-history li {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
    }

    .chat-history li[data-from="player"] {
      align-items: flex-end;
      text-align: right;
    }

    .chat-history li[data-from="companion"] {
      align-items: flex-start;
      text-align: left;
    }

    .msg-from {
      font-size: var(--type-3xs);
      color: var(--gold);
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    .msg-text {
      background: color-mix(in srgb, var(--surface-card-2) var(--alpha-high), var(--bg));
      padding: var(--space-xs) var(--space-sm);
      border-radius: 0.5rem;
      font-size: 0.92rem;
      line-height: 1.45;
      color: var(--text-prose);
      max-width: 85%;
      margin: 0;
      border: 1px solid var(--border);
    }

    li[data-from="companion"] .msg-text {
      border-color: color-mix(in srgb, var(--gold) var(--alpha-low), var(--border));
    }

    .msg-time {
      font-size: var(--type-3xs);
      color: var(--text-dim);
      margin-top: 0.125rem;
    }

    li[data-role="typing"] {
      color: var(--gold);
      font-style: italic;
      font-size: var(--type-xs);
      padding: var(--space-xs);
      text-align: center;
    }

    li[data-role="error"] {
      color: var(--danger);
      font-size: var(--type-xs);
      text-align: center;
      padding: var(--space-xs);
      background: color-mix(in srgb, var(--danger) var(--alpha-subtle), transparent);
      border-radius: 0.25rem;
    }

    .chat-input-form {
      display: flex;
      gap: var(--space-xs);
      padding-top: var(--space-xs);
      border-top: 1px solid var(--border);
    }

    .chat-input-form input {
      flex: 1;
      background: var(--bg);
      border: 1px solid var(--border);
      color: var(--text-prose);
      padding: var(--space-sm);
      border-radius: 0.25rem;
      outline: none;
      font-family: inherit;
    }

    .chat-input-form input:focus {
      border-color: var(--gold);
    }

    .chat-input-form button {
      padding: 0 var(--space-md);
      background: var(--surface-card-2);
      border: 1px solid var(--border);
      color: var(--gold);
      font-weight: 800;
      font-size: var(--type-xs);
      border-radius: 0.25rem;
      cursor: pointer;
    }

    @media (max-width: 30rem) {
      .float-media {
        float: none;
        width: 100% !important;
        margin-right: 0;
      }
    }
  </style>
</companion-chat-dialog>
